import {
  useCallback,
  useEffect,
  useSyncExternalStore,
} from 'react';

import { useAuth } from '@/contexts/AuthContext';

import {
  ensureCommandCenterAuthoritySession,
  isCommandCenterAuthoritySessionSnapshotFresh,
  readCommandCenterAuthoritySession,
  resetCommandCenterAuthoritySession,
  retryCommandCenterAuthoritySession,
  subscribeCommandCenterAuthoritySession,
  type CommandCenterAuthoritySessionStatus,
} from '@/governor/routing/CommandCenterAuthoritySession';

export interface CommandCenterAuthorityState {
  readonly allowed: boolean;
  readonly loading: boolean;
  readonly error: string | null;

  readonly status:
    CommandCenterAuthoritySessionStatus;

  readonly retry: () => void;
}

export function useCommandCenterAuthority(
  enabled = true,
): CommandCenterAuthorityState {
  const {
    isAuthenticated,
    user,
    loading: authLoading,
  } = useAuth();

  const snapshot =
    useSyncExternalStore(
      subscribeCommandCenterAuthoritySession,
      readCommandCenterAuthoritySession,
      readCommandCenterAuthoritySession,
    );

  useEffect(
    () => {
      if (authLoading) {
        return;
      }

      if (
        !isAuthenticated ||
        !user?.id
      ) {
        resetCommandCenterAuthoritySession();
        return;
      }

      if (!enabled) {
        return;
      }

      void ensureCommandCenterAuthoritySession(
        user.id,
      );
    },
    [
      enabled,
      authLoading,
      isAuthenticated,
      user?.id,
    ],
  );

  const sameUser =
    !!user?.id &&
    snapshot.userId ===
      user.id;

  const freshFinal =
    sameUser &&
    !!user?.id &&
    isCommandCenterAuthoritySessionSnapshotFresh(
      snapshot,
      user.id,
    );

  useEffect(
    () => {
      if (
        authLoading ||
        !enabled ||
        !isAuthenticated ||
        !user?.id ||
        !sameUser ||
        freshFinal ||
        (
          snapshot.status !== 'ALLOWED' &&
          snapshot.status !== 'DENIED'
        )
      ) {
        return;
      }

      void ensureCommandCenterAuthoritySession(
        user.id,
      );
    },
    [
      enabled,
      authLoading,
      isAuthenticated,
      user?.id,
      sameUser,
      freshFinal,
      snapshot.status,
    ],
  );

  const status:
    CommandCenterAuthoritySessionStatus =
    !enabled ||
    !isAuthenticated ||
    !user?.id
      ? 'IDLE'
      : sameUser
        ? (
            (
              snapshot.status === 'ALLOWED' ||
              snapshot.status === 'DENIED'
            ) &&
            !freshFinal
              ? 'VERIFYING'
              : snapshot.status
          )
        : 'VERIFYING';

  const loading =
    enabled &&
    (
      authLoading ||
      (
        isAuthenticated &&
        !!user?.id &&
        (
          !sameUser ||
          status === 'IDLE' ||
          status === 'VERIFYING'
        )
      )
    );

  const allowed =
    enabled &&
    sameUser &&
    status === 'ALLOWED';

  const error =
    enabled &&
    sameUser &&
    status === 'ERROR'
      ? snapshot.error
      : null;

  const retry =
    useCallback(
      () => {
        if (
          !enabled ||
          authLoading ||
          !isAuthenticated ||
          !user?.id
        ) {
          return;
        }

        void retryCommandCenterAuthoritySession(
          user.id,
        );
      },
      [
        enabled,
        authLoading,
        isAuthenticated,
        user?.id,
      ],
    );

  return {
    allowed,
    loading,
    error,
    status,
    retry,
  };
}
